import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { listAgreements, deleteAgreement, type Agreement } from '@/lib/api/agreements';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, LargeTitleHeader, SegmentedControl, PullToRefresh, SwipeableRow } from '@/components/common';
import type { SwipeAction } from '@/components/common';
import { agreementPath } from '@/lib/utils/navPaths';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft:     { label: 'Draft',     color: 'text-gray-600 bg-gray-100 border-gray-200' },
  sent:      { label: 'Sent',      color: 'text-blue-700 bg-blue-50 border-blue-200' },
  signed:    { label: 'Signed',    color: 'text-green-700 bg-green-50 border-green-200' },
  cancelled: { label: 'Cancelled', color: 'text-red-700 bg-red-50 border-red-200' },
};

const fmt = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtMoney = (v: number | null) =>
  v == null ? '—' : `$${Number(v).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type AgreementFilter = '' | 'draft' | 'sent' | 'signed' | 'cancelled';

const AGREEMENT_FILTER_OPTIONS: { value: AgreementFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'signed', label: 'Signed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const AgreementsPage = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, toast } = useToast();
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<AgreementFilter>('');
  const [confirmDelete, setConfirmDelete] = useState<Agreement | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listAgreements(filterStatus ? { status: filterStatus } : undefined);
      setAgreements(data);
    } catch {
      toast.error('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteAgreement(confirmDelete.id);
      setConfirmDelete(null);
      load();
      toast.success('Agreement deleted');
    } catch {
      toast.error('Failed to delete agreement');
    }
  };

  return (
    <MainLayout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="p-4 md:p-8 space-y-4">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Agreements"
          subtitle="Resources"
          actions={
            <button
              onClick={() => navigate('/agreements/new')}
              className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add</span>
              <span className="hidden sm:inline">New Agreement</span>
            </button>
          }
        >
          <SegmentedControl<AgreementFilter>
            options={AGREEMENT_FILTER_OPTIONS}
            value={filterStatus}
            onChange={setFilterStatus}
            size="sm"
          />
        </LargeTitleHeader>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          </div>
        ) : agreements.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">handshake</span>
            <p className="text-gray-500 font-medium">No {filterStatus || ''} agreements</p>
            {!filterStatus && (
              <button
                onClick={() => navigate('/agreements/new')}
                className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Create your first agreement
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <PullToRefresh onRefresh={load}>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Number</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Title</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden md:table-cell">Client</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 hidden lg:table-cell">Date</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Value</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {agreements.map(a => {
                      const sc = STATUS_CONFIG[a.status];
                      return (
                        <tr
                          key={a.id}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => navigate(agreementPath(a.client_id, a.id))}
                        >
                          <td className="px-4 py-3 font-mono text-xs text-primary font-semibold">{a.agreement_number}</td>
                          <td className="px-4 py-3 font-medium text-gray-900 max-w-xs">
                            <div className="flex items-center gap-2">
                              <p className="truncate">{a.title}</p>
                              {a.agreement_type === 'subcontractor_work_order' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-200 font-semibold flex-shrink-0">WO</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{a.client_display_name || a.client_name}</td>
                          <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{fmt(a.agreement_date)}</td>
                          <td className="px-4 py-3 text-gray-700 font-medium">{fmtMoney(a.contract_value)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${sc.color}`}>{sc.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={e => { e.stopPropagation(); if (a.status !== 'signed') setConfirmDelete(a); }}
                              disabled={a.status === 'signed'}
                              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title={a.status === 'signed' ? 'Signed agreements cannot be deleted' : 'Delete'}
                            >
                              <span className="material-symbols-outlined text-base">delete</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile SwipeableRow list */}
              <div className="sm:hidden divide-y divide-gray-100">
                {agreements.map(a => {
                  const sc = STATUS_CONFIG[a.status];
                  const rowActions: SwipeAction[] = [
                    ...(a.status !== 'signed' ? [{
                      key: 'delete',
                      label: 'Delete',
                      icon: 'delete',
                      variant: 'danger' as const,
                      onClick: () => setConfirmDelete(a),
                    }] : []),
                  ];
                  return (
                    <SwipeableRow key={a.id} actions={rowActions} disabled={a.status === 'signed'}>
                      <div className="px-4 py-3.5 cursor-pointer" onClick={() => navigate(agreementPath(a.client_id, a.id))}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-mono text-xs text-primary font-semibold">{a.agreement_number}</p>
                            <p className="font-medium text-gray-900 text-sm truncate">{a.title}</p>
                            <p className="text-xs text-gray-500 truncate">{a.client_display_name || a.client_name}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-gray-900 text-sm">{fmtMoney(a.contract_value)}</p>
                            <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border font-medium ${sc.color}`}>{sc.label}</span>
                          </div>
                        </div>
                      </div>
                    </SwipeableRow>
                  );
                })}
              </div>
            </PullToRefresh>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-gray-900 mb-2">Delete Agreement?</h3>
            <p className="text-sm text-gray-600 mb-5">
              <span className="font-medium">{confirmDelete.agreement_number}</span> — {confirmDelete.title} will be permanently deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};
